/**
 * Shared loader for scene images fetched into raw `Image()` elements
 * (background + screen texture). Adds one automatic retry (transient mobile
 * network blips usually recover) and, on final failure, probes the URL with
 * `fetch` so the error report can say WHY the load failed — `img.onerror`
 * itself carries no status (403 = expired presigned URL, 5xx = storage/proxy,
 * fetch throw = network down).
 */

export type ImageLoadFailure = {
  status?: number
  fetchError?: string
  online: boolean
  attempts: number
}

const RETRY_DELAY_MS = 1500

const diagnoseFailure = async (url: string): Promise<Pick<ImageLoadFailure, 'status' | 'fetchError'>> => {
  try {
    const res = await fetch(url, {cache: 'no-store'})
    // Status is all we need — cancel the body so we don't re-download the image.
    void res.body?.cancel()
    return {status: res.status}
  } catch (e) {
    return {fetchError: e instanceof Error ? e.message : String(e)}
  }
}

/**
 * Load `url` into an `HTMLImageElement` with `crossOrigin='anonymous'`.
 * Retries once before giving up. Returns a cleanup function — after it runs,
 * neither callback fires (safe for effect teardown).
 */
export const loadSceneImage = (
  url: string,
  onLoad: (img: HTMLImageElement) => void,
  onError: (failure: ImageLoadFailure) => void,
): (() => void) => {
  let disposed = false
  let attempts = 0
  let retryTimer: number | undefined

  const start = () => {
    attempts++
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (disposed) return
      onLoad(img)
    }
    img.onerror = () => {
      if (disposed) return
      if (attempts === 1) {
        // Same URL on purpose: presigned query params can't be cache-busted
        // without invalidating the signature, and browsers don't cache failures.
        retryTimer = window.setTimeout(start, RETRY_DELAY_MS)
        return
      }
      void diagnoseFailure(url).then((diag) => {
        if (disposed) return
        onError({...diag, online: navigator.onLine, attempts})
      })
    }
    img.src = url
  }

  start()
  return () => {
    disposed = true
    if (retryTimer !== undefined) window.clearTimeout(retryTimer)
  }
}
